import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const MedicinesManagement = () => (
  <Page
    title="Medicines Management"
    intro="Medicines management covers drug bag locations, checks, administration, the controlled drug register, and the audits and discrepancy workflow that keep controlled drug handling accountable."
  >
    <Section title="Configuration">
      <p>
        Medicines are configured from{' '}
        <DocLink url="/site-configuration/medicines" text="Configuration > Governance > Medicines" />
        . The page is split into three areas: Formulary, Stores and bags, and
        Bag levels.
      </p>

      <Step number={1} title="Maintain the formulary">
        <p>
          The formulary defines each medicine name, constitution, presentation,
          legal basis, governance flags, and the standard stores and bag alarm
          levels. The standard bag alarm is the default threshold used for any
          bag level that does not have a custom override.
        </p>
      </Step>

      <Step number={2} title="Create stores and bags">
        <p>
          Stores are stock-holding locations. Medicine bags are operational
          locations and must be assigned to a bag level. The bag level controls
          which formulary items are expected in that bag.
        </p>
        <p>
          A store can be marked with <strong>Allow out of date medicines</strong>{' '}
          to act as a quarantine location - for example, holding expired stock
          safely until it is collected for disposal. These stores show an{' '}
          <strong>OOD holding</strong> tag in the locations list.
        </p>
        <Info title="Out of date holding stores and KPIs">
          <p>
            Expired medicines held in a store that allows out of date medicines
            are excluded from expiry KPIs, dashboard counts, and the expired
            warning badge on that store - they are expected to be there. In-date
            stock in the same store still counts towards expiring-soon warnings,
            and the store contents view still highlights expired items so staff
            can see what is awaiting disposal.
          </p>
        </Info>
      </Step>

      <Step number={3} title="Manage bag levels">
        <p>
          Bag levels are the clinical capability levels used by the formulary
          and bags. Add or rename levels, choose their colour, and review how
          many bags and formulary items currently use each level.
        </p>
      </Step>
    </Section>

    <Section title="Bag level alarms">
      <p>
        By default, every medicine bag uses the medicine's standard bag alarm
        from the formulary. If a specific bag level needs different stock
        thresholds, enable <strong>Custom alarms</strong> on that bag level and
        open <strong>Manage</strong>.
      </p>

      <Bullets>
        <Bullet>
          Enter a custom bag alarm beside only the medicines that need a
          different threshold for that bag level.
        </Bullet>
        <Bullet>
          Leave a medicine blank to keep using its standard formulary bag alarm.
        </Bullet>
        <Bullet>
          Custom alarms are applied when viewing bag contents and when running a
          medicines audit, so discrepancies are checked against the correct
          threshold for that bag level.
        </Bullet>
      </Bullets>

      <Info title="Fallback behaviour">
        <p>
          Custom alarms do not replace the formulary alarm globally. They only
          override the bag alarm for bags assigned to that bag level. Stores
          continue to use the formulary stores alarm.
        </p>
      </Info>
    </Section>

    <Section title="Locations, bags, and stock">
      <Screenshot url="governance/meds-management.png">
        The Medicines Management page with medicine locations, drug bags, and
        their stock warnings.
      </Screenshot>
      <Bullets>
        <Bullet>
          Open <DocLink url="/medicines-management" text="Medicines Management" />{' '}
          for medicine locations and drug bags, or{' '}
          <DocLink url="/checkmedsbag" text="Check Medicine Bag" /> to run a
          bag check.
        </Bullet>
        <Bullet>An all-stock view lists every medicine across locations.</Bullet>
        <Bullet>
          The level column in the formulary shows <strong>All</strong> when a
          medicine applies to every configured level.
        </Bullet>
        <Bullet>
          Selecting one or more batches of a medicine opens a single drawer
          for everything you can do with that selection - use "select all
          expired"/"select all expiring soon" to quickly pick a batch, then
          move it to another bag or store, discard an empty reusable
          container, remove stock with a reason, or (for stores only) delete
          it from the system with a reason. Bags and stores themselves can
          also be re-assigned to a different vehicle or store from their
          current-location control.
        </Bullet>
        <Bullet>
          Removing stock is not limited to expired items: Governance can
          remove in-date stock too, choosing a required reason - Out of date
          or Expiring soon where they fit the selection, or Broken / damaged,
          Wrong batch number or expiry recorded, Contaminated / seal
          compromised, or Other with a note. Removing a controlled drug
          requires a witness, and items currently held by a draft patient
          record or drawn up into a syringe cannot be removed.
        </Bullet>
        <Bullet>
          Expired batches that must be kept until disposal can be moved into a
          store marked <strong>Allow out of date medicines</strong> - once
          there, they stop counting against expiry KPIs and alerts.
        </Bullet>
        <Bullet>
          Every Monday morning the Governance team receives a{' '}
          <strong>medicines expiry digest</strong> - a summary of stock that
          has expired or expires within 30 days, broken down by location, with
          a link to the expiry dashboard. Quarantined expired stock is
          excluded. Individuals can switch the digest off in their
          notification preferences.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Administration">
      <p>
        Medicine administration is selected and recorded from{' '}
        <DocLink
          url="/medicines-management/administration"
          text="Medicine Administration"
        />
        , with a history available at{' '}
        <DocLink
          url="/medicines-management/administration-records"
          text="Administration Records"
        />
        .
      </p>
      <Info title="Who can do this?">
        Any signed-in user can run bag checks, view bag contents, and record
        an administration. Corrections, voids, formulary and stock
        configuration, deleting stock, archiving locations, reviewing medicine
        audits, the controlled drug register, and managing discrepancies
        require the Governance team (site administrators always have access).
      </Info>
      <Bullets>
        <Bullet>
          <strong>Correct</strong> an administration when the drug was
          genuinely given but a detail was recorded wrong (patient, dose, or
          time) - this requires a reason, leaves stock untouched, and never
          edits the original record, which stays visible and is flagged as
          corrected.
        </Bullet>
        <Bullet>
          <strong>Void</strong> an administration when it was a pure recording
          error and nothing was actually given - this requires a reason and
          returns the stock to available inventory. Voiding a controlled drug
          administration additionally requires naming a witness. Changing
          which medicine/batch/bag was actually consumed isn't possible as a
          correction - void it and record it again instead.
        </Bullet>
      </Bullets>
      <Info title="Draft patient records lock their stock">
        Stock recorded against a patient record is held by that record while
        it is still a draft - held items cannot be moved, removed, or counted
        out elsewhere until the record is submitted or the entry is removed.
        Medicine administrations recorded on an ePCR can be corrected or
        voided only while the record is a draft; once it is submitted, the
        administration record is final.
      </Info>
      <Info title="Patient details need step-up authentication">
        The administration records list only includes the linked patient
        record details (name, date of birth, ePCR number) once you have
        completed multi-factor authentication in the current session -
        without it, the clinical audit trail is shown without patient
        identifiers.
      </Info>
    </Section>

    <Section title="Drawn-up medicines">
      <p>
        Clinicians working in time-critical settings can draw medicines up
        into syringes in advance. Drawing up moves the stock out of its bag
        and into the syringe, so bag counts and the controlled drug register
        stay accurate while the syringe is carried.
      </p>
      <Bullets>
        <Bullet>
          Staff see the syringes they are carrying at{' '}
          <DocLink url="/drawn-up-medicines" text="My Drawn-up Medicines" />,
          and Governance has oversight of every drawn-up syringe - who holds
          it and when it expires - at{' '}
          <DocLink
            url="/medicines-management/drawn-up"
            text="Drawn-up Medicines"
          />
          .
        </Bullet>
        <Bullet>
          A drawn-up syringe can be <strong>administered</strong> (a partial
          administration leaves the remainder held by the clinician),{' '}
          <strong>handed over</strong> to another clinician, or{' '}
          <strong>discarded</strong> - a discard marks the remaining stock as
          disposed.
        </Bullet>
        <Bullet>
          Drawn-up stock still counts in employee CD audits: the expected
          quantity for an AmbuNet-stock audit includes any drawn-up syringes
          the clinician holds.
        </Bullet>
      </Bullets>
      <Screenshot url="governance/drawn-up-medicines.png">
        The Drawn-up Medicines oversight list showing every syringe, who holds
        it, and when it expires.
      </Screenshot>
      <Info title="Who can do this?">
        Any signed-in user can draw up, administer, hand over, and discard
        their own syringes. The organisation-wide drawn-up list requires the
        Governance team (site administrators always have access).
      </Info>
    </Section>

    <Section title="Medicines in incident reports">
      <p>
        When reporting an incident that involves medicines, they don't
        always need to be removed from a bag - a medicine might be involved
        because it was already administered or already removed some other
        way. The incident form separates the two: pick medicines to actually
        remove from a specific bag (with a quantity, e.g. accidental
        destruction), and separately reference any other medicines involved
        without forcing a removal.
      </p>
    </Section>

    <Section title="Audits and controlled drugs">
      <p>
        Drug bag audits and controlled drug audits are recorded against a bag
        or an employee and reviewed alongside general medicines management —
        this is separate from the tier 2 governance audit templates covered
        in{' '}
        <DocLink
          docsPage
          url="/clinical-governance/governance-audits"
          text="Audits, Risk, and KPIs"
        />
        .
      </p>
      <Bullets>
        <Bullet>
          <strong>Bag audits</strong> count a bag's contents batch by batch
          against expected stock (web or mobile). Missing and surplus items can
          be reconciled as part of the audit, with each change logged, and
          Governance reviews every completed audit.
        </Bullet>
        <Bullet>
          <strong>Employee CD audits</strong> have two modes.{' '}
          <strong>AmbuNet stock</strong> audits a drug from the formulary held
          in the employee's assigned bags: the expected quantity is calculated
          from live stock (including drawn-up syringes they hold) and can only
          be adjusted with a reason. <strong>Personal CDs</strong> covers
          controlled drugs the clinician holds outside AmbuNet, with the
          expected quantity entered manually. Both require a witness.
        </Bullet>
        <Bullet>
          <strong>Seal scanning</strong> - if your drug bag seals carry a QR
          or data-matrix code, scan it during a bag audit or vehicle check
          instead of reading the number by eye: the system compares the
          scanned code against the recorded seal and marks the check{' '}
          <strong>verified by scan</strong> on the audit. Scanning also fills
          in the new seal number when re-sealing a bag.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Controlled drug register">
      <p>
        The{' '}
        <DocLink
          url="/medicines-management/cd-register"
          text="Controlled Drug Register"
        />{' '}
        shows a live balance for every controlled drug, per bag and store.
        Balances are counted from the stock records themselves rather than
        stored, so the latest balance always reconciles against physical
        stock.
      </p>
      <Bullets>
        <Bullet>
          Open any holding to see its full ledger - receipts, moves,
          administrations, destructions and draw-ups, each attributed with a
          timestamp, clinician, and witness - with a running balance per entry.
        </Bullet>
        <Bullet>
          Corrections and voids stay visible in the ledger: a corrected entry
          is shown struck through next to its replacement, and a voided
          administration's stock returns to the balance.
        </Bullet>
        <Bullet>
          <strong>Export for inspection</strong> produces a PDF of one drug's
          register at one location, laid out like a paper CD register (date,
          entry, in, out, balance, clinician, witness).
        </Bullet>
      </Bullets>
      <Info title="Balances exact from a marked date">
        Register entries older than the structured record format show without
        a balance column, and the ledger marks the date exact balances begin.
        The current balance is always live, whatever the history holds.
      </Info>
    </Section>

    <Section title="Discrepancies">
      <p>
        When a bag audit or an employee CD audit finds a count that doesn't
        match the expected stock, a <strong>discrepancy record</strong> is
        raised automatically and the Governance team is notified. Open
        discrepancies are listed at{' '}
        <DocLink
          url="/medicines-management/discrepancies"
          text="Medicine Discrepancies"
        />
        .
      </p>
      <Bullets>
        <Bullet>
          Each discrepancy moves through <strong>Open</strong>,{' '}
          <strong>Under Investigation</strong> and <strong>Resolved</strong>,
          with an assignable owner and an update trail. Resolving requires a
          resolution note - a mismatch can never be silently absorbed.
        </Bullet>
        <Bullet>
          Reconciling the stock during the audit does not resolve the
          discrepancy - the record stays open until Governance explains and
          closes it.
        </Bullet>
        <Bullet>
          A discrepancy can be escalated to a pre-filled incident in one
          click if one wasn't already raised during the audit.
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default MedicinesManagement
